/**
 * Production adapter for `fill_cavities`: the SAME calls the toolbar's Fill
 * cavities button makes — `SDFOperations.applyFillCavities` on the
 * structure's texture, then `roi.replaceTexture(result, 'Fill Cavities')`,
 * a contour-layer view update, and the button's success snackbar (same
 * message ID) — shared in {@link makeInPlaceEngineDeps}.
 * So the agent overwrites the structure exactly as the button does, with the
 * same undo step. The engine is a SYSTEM BOUNDARY: this glue is verified by
 * the manual demo; the logic it feeds is unit-tested through fakes.
 */
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { SDFOperations } from '~/engine/util/algorithms/sdf/SDFOperations';
import { environmentState } from '~/state/atoms/engineAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import {
    makeInPlaceEngineDeps,
    requireSourceTexture,
    toComputedGeometry,
} from '../core';
import type { FillCavitiesDeps } from './toolRunner';

export function useFillCavitiesEngineDeps(): () => Omit<
    FillCavitiesDeps,
    'pendingMutationExists'
> {
    const env = useRecoilValue(environmentState);
    const structureSet = useRecoilValue(currentStructureSetState);
    const intl = useIntl();

    return useCallback(() => {
        const base = makeInPlaceEngineDeps(
            env,
            structureSet,
            'Fill Cavities',
            (name) =>
                intl.formatMessage(
                    { id: 'postProcessing.fillCavities.success' },
                    { name }
                )
        );
        return {
            ...base,
            geometry: {
                ...base.geometry,
                async applyFillCavities(roiNumber: number) {
                    const src = requireSourceTexture(env, roiNumber);
                    const result = await SDFOperations.applyFillCavities(src);
                    // undefined = another contour operation holds the GL
                    // guard, or the source had no readable SDF data.
                    if (!result) {
                        throw new Error(
                            'fill cavities could not run (another contour ' +
                                'operation may be in progress); try again'
                        );
                    }
                    return toComputedGeometry(result);
                },
            },
        };
    }, [env, structureSet, intl]);
}
